import designHeroVideo from '../assets/design-hero-video.webm';
import strategyHeroVideo from '../assets/strategy-hero-video.webm';
import researchHeroVideo from '../assets/research-hero-video.webm';
import designHeroPoster from '../assets/posters/design-hero-video-poster.jpg';
import strategyHeroPoster from '../assets/posters/strategy-hero-video-poster.jpg';
import researchHeroPoster from '../assets/posters/research-hero-video-poster.jpg';

export const serviceDetails = {
  design: {
    slug: 'design',
    title: 'Design',
    seoTitle: 'UI/UX Design Company for Enterprise, SaaS & FinTech',
    seoDescription:
      'Product design, UI design, design systems, and prototyping for enterprise, fintech, AI, and SaaS companies. Design complex products people actually enjoy using.',
    seoKeywords:
      'ui ux design company, ui ux design agency, ui ux design firm, ui and ux company, saas ui ux design, saas ui design, fintech ui ux design, web app ux design, enterprise product design, design systems consulting',
    heroVideo: designHeroVideo,
    heroPoster: designHeroPoster,
    headline: [
      { text: 'Design for', tone: 'muted', italic: true },
      { text: 'complex products', tone: 'gradient' },
      { text: 'people actually enjoy.', tone: 'plain' },
    ],
    subtitle:
      'From wireframes to full design systems, we design enterprise-grade interfaces that simplify complexity without sacrificing craft.',
    services: [
      'Product Design',
      'UI Design',
      'Design Systems',
      'Prototyping',
      'Interaction Design',
      'Information Architecture',
    ],
    ctaEyebrow: 'Stop guessing. Start shipping.',
    ctaHeadline: [
      { text: "Let's design your", tone: 'plain' },
      { text: 'next great product.', tone: 'gradient' },
    ],
    ctaSubtitle:
      'Book a UX consultation and uncover exactly where your product can improve, and what to do about it.',
    process: [
      {
        icon: 'TelescopeIcon',
        title: 'Discover',
        description: 'Stakeholder interviews, UX audits, and a review of the competitive landscape.',
      },
      {
        icon: 'LightBulbIcon',
        title: 'Define',
        description: 'Synthesize findings into problem statements, personas, and success metrics.',
      },
      {
        icon: 'PencilIcon',
        title: 'Design',
        description: 'Wireframes, prototypes, and design systems built to scale with your product.',
      },
      {
        icon: 'BeakerIcon',
        title: 'Validate',
        description: 'Usability testing and iteration before anything ships to production.',
      },
      {
        icon: 'RocketIcon',
        title: 'Deliver',
        description: 'Developer handoff, QA support, and documentation your team can maintain.',
      },
    ],
  },
  strategy: {
    slug: 'strategy',
    title: 'Strategy',
    seoTitle: 'Enterprise UX Consulting & UX Strategy Services',
    seoDescription:
      'UX strategy, design ops, roadmapping, and fractional design leadership for enterprise, fintech, and AI product teams ready to scale.',
    seoKeywords:
      'enterprise ux consulting, ux consulting companies, ux for enterprise applications, enterprise user experience, UX strategy consulting, design ops consulting, fractional design leader, best ux companies',
    heroVideo: strategyHeroVideo,
    heroPoster: strategyHeroPoster,
    headline: [
      { text: 'Strategy for', tone: 'muted', italic: true },
      { text: 'ambitious product teams', tone: 'gradient' },
      { text: 'ready to scale.', tone: 'plain' },
    ],
    subtitle:
      'We align stakeholders, prioritize what matters, and build roadmaps your team can actually execute.',
    services: [
      'UX Strategy',
      'Design Ops',
      'Enterprise Consulting',
      'Fractional Design Lead',
      'Roadmapping',
      'AI Product Design',
    ],
    ctaEyebrow: 'Less noise. More clarity.',
    ctaHeadline: [
      { text: "Let's build a roadmap", tone: 'plain' },
      { text: 'you can execute.', tone: 'gradient' },
    ],
    ctaSubtitle:
      'Book a strategy consultation and walk away with a prioritized plan your whole team can rally behind.',
    process: [
      {
        icon: 'SearchIcon',
        title: 'Assess',
        description: 'Audit your current product, business goals, and competitive position.',
      },
      {
        icon: 'PeopleIcon',
        title: 'Align',
        description: 'Workshops with stakeholders to define a shared vision and success metrics.',
      },
      {
        icon: 'ProjectRoadmapIcon',
        title: 'Roadmap',
        description: 'Prioritized initiatives sequenced against business impact and effort.',
      },
      {
        icon: 'GearIcon',
        title: 'Govern',
        description: 'Design ops, rituals, and decision frameworks that keep teams aligned.',
      },
      {
        icon: 'GraphIcon',
        title: 'Measure',
        description: 'KPIs and feedback loops that inform the next roadmap cycle.',
      },
    ],
  },
  research: {
    slug: 'research',
    title: 'Research',
    seoTitle: 'UX Audits, User Research & Usability Testing Services',
    seoDescription:
      'UX audits, user research, usability testing, journey mapping, and accessibility audits for enterprise, fintech, and SaaS products. Evidence-based UX decisions.',
    seoKeywords:
      'ux audit company, ux audit services, ux testing services, user research agency, usability testing services, journey mapping, accessibility audit, enterprise UX research, fintech user research',
    heroVideo: researchHeroVideo,
    heroPoster: researchHeroPoster,
    headline: [
      { text: 'Research for', tone: 'muted', italic: true },
      { text: 'products that need', tone: 'gradient' },
      { text: 'real answers.', tone: 'plain' },
    ],
    subtitle:
      'We talk to your users so you don’t have to guess. Every decision backed by evidence, not opinion.',
    services: [
      'UX Audits',
      'User Research',
      'Usability Testing',
      'Journey Mapping',
      'Competitive Analysis',
      'Accessibility Audits',
      'Analytics & Insights',
    ],
    ctaEyebrow: 'Stop assuming. Start knowing.',
    ctaHeadline: [
      { text: "Let's find out what your", tone: 'plain' },
      { text: 'users actually need.', tone: 'gradient' },
    ],
    ctaSubtitle:
      'Book a research consultation and get real answers about how people use your product.',
    process: [
      {
        icon: 'NoteIcon',
        title: 'Plan',
        description: 'Define research questions, methodology, and success criteria.',
      },
      {
        icon: 'PersonIcon',
        title: 'Recruit',
        description: 'Source participants that match your real user base, not a convenience sample.',
      },
      {
        icon: 'CommentDiscussionIcon',
        title: 'Conduct',
        description: 'Interviews, usability tests, and contextual inquiry with real users.',
      },
      {
        icon: 'StackIcon',
        title: 'Synthesize',
        description: 'Affinity mapping and thematic analysis to surface real patterns.',
      },
      {
        icon: 'ChecklistIcon',
        title: 'Report',
        description: 'Actionable insights delivered directly to your design and product teams.',
      },
    ],
  },
};

export const serviceOrder = ['design', 'strategy', 'research'];
